import {
  BODY_FONT,
  HEADER_FILL,
  LINK_FONT,
  NA_FILL,
  TITLE_FONT,
  VALUE_FILL,
  hideRowsThrough,
} from "./detailSheetCommon.js";

const STAKEHOLDER_GROUP_SHEET = "Stakeholder group";
const PRINCIPLE_TITLE =
  "PRINCIPLE 4: Businesses should respect the interests of and be responsive to all its stakeholders";

const COL_D = 4;
const COL_E = 5;

const THIN = { style: "thin", color: { argb: "FF000000" } };
const MEDIUM = { style: "medium", color: { argb: "FF000000" } };

const SECTION_FONT = { name: "Calibri", size: 12, bold: true };

const BORDER_PATTERNS = {
  principleTitle: ["mmt-", "m-tm"],
  sectionBand: ["tmt-", "t-tm"],
  labelValue: ["tmtt", "tttm"],
  notes: ["-mmt", "--mm"],
};

const LABELS = {
  essential: "Essential Indicators",
  q1: "1. Describe the processes for identifying key stakeholder groups of the entity.",
  q2: "2. List stakeholder groups identified as key for your entity and the frequency of engagement with each stakeholder group.",
  leadership: "Leadership Indicators",
  lq1:
    "1. Provide the processes for consultation between stakeholders and the Board on economic, environmental, and social topics or if consultation is delegated, how is feedback from such consultations provided to the Board.",
  lq2:
    "2.Whether stakeholder consultation is used to support the identification and management of environmental, and social topics.",
  lq2Details:
    "If so, provide details of instances as to how the inputs received from stakeholders on these topics were incorporated into policies and activities of the entity.",
  lq3:
    "3. Provide details of instances of engagement with, and actions taken to, address the concerns of vulnerable/ marginalized stakeholder groups.",
  notes: "Notes",
};

function parseBorderSpec(spec) {
  const map = { t: THIN, m: MEDIUM };
  return {
    top: spec[0] !== "-" ? map[spec[0]] : undefined,
    left: spec[1] !== "-" ? map[spec[1]] : undefined,
    bottom: spec[2] !== "-" ? map[spec[2]] : undefined,
    right: spec[3] !== "-" ? map[spec[3]] : undefined,
  };
}

function setCell(ws, row, col, value, options = {}) {
  const cell = ws.getCell(row, col);
  if (options.link) {
    cell.value = { text: value || "Add Details", hyperlink: options.link };
    cell.font = LINK_FONT;
  } else {
    cell.value = value ?? "";
    cell.font = options.font || (options.bold ? { ...BODY_FONT, bold: true } : BODY_FONT);
  }
  if (options.border) cell.border = options.border;
  else if (options.bordered !== false) cell.border = parseBorderSpec("tttt");
  cell.alignment = {
    horizontal: options.align || "left",
    vertical: options.vertical || "middle",
    wrapText: options.wrapText !== false,
  };
  if (options.fill) cell.fill = options.fill;
}

function safeText(value, fallback = "") {
  if (value === null || value === undefined || value === "") return fallback;
  if (Array.isArray(value)) return value.length ? value.join(", ") : fallback;
  if (typeof value === "object") return fallback;
  return String(value);
}

function toYesNo(value) {
  if (value === true || value === "true" || value === "yes" || value === "Yes") return "Yes";
  if (value === false || value === "false" || value === "no" || value === "No") return "No";
  return safeText(value);
}

function setColumnWidths(ws) {
  const widths = {
    A: 9.1796875,
    B: 9.1796875,
    C: 9.1796875,
    D: 150.7265625,
    E: 20,
    F: 9.1796875,
    G: 9.1796875,
    H: 9.1796875,
    I: 9.1796875,
  };
  Object.entries(widths).forEach(([col, width]) => {
    ws.getColumn(col).width = width;
  });
  ["A", "B", "I"].forEach((col) => {
    ws.getColumn(col).hidden = true;
  });
  for (let col = 10; col <= 16384; col += 1) {
    ws.getColumn(col).hidden = true;
  }
}

function setupSheetView(workbook, ws) {
  ws.views = [
    {
      showGridLines: false,
      state: "frozen",
      xSplit: 0,
      ySplit: 8,
      topLeftCell: "A9",
      zoomScale: 85,
      zoomScaleNormal: 85,
    },
  ];
  if (!workbook.views?.length) {
    workbook.views = [{ showGridLines: false }];
  } else {
    workbook.views[0].showGridLines = false;
  }
  for (let row = 1; row <= 5; row += 1) {
    const excelRow = ws.getRow(row);
    excelRow.hidden = true;
    excelRow.height = 0;
  }
}

function applyCellBorders(ws, row, specs) {
  specs.forEach((spec, index) => {
    ws.getCell(row, COL_D + index).border = parseBorderSpec(spec);
  });
}

function addPrincipleTitle(ws, row) {
  // Keep D/E unmerged so each cell retains its own border (ExcelJS collapses borders on merge).
  setCell(ws, row, COL_D, PRINCIPLE_TITLE, {
    font: TITLE_FONT,
    align: "center",
    fill: HEADER_FILL,
    bordered: false,
  });
  setCell(ws, row, COL_E, "", {
    font: TITLE_FONT,
    align: "center",
    fill: HEADER_FILL,
    bordered: false,
  });
  applyCellBorders(ws, row, BORDER_PATTERNS.principleTitle);
  ws.getRow(row).height = 30;
  return row + 1;
}

function addSectionBand(ws, row, title) {
  setCell(ws, row, COL_D, title, {
    font: SECTION_FONT,
    align: "center",
    fill: NA_FILL,
    bordered: false,
  });
  setCell(ws, row, COL_E, "", {
    font: SECTION_FONT,
    align: "center",
    fill: NA_FILL,
    bordered: false,
  });
  applyCellBorders(ws, row, BORDER_PATTERNS.sectionBand);
  ws.getRow(row).height = 15.5;
  return row + 1;
}

function addLabelValueRow(ws, row, label, value, options = {}) {
  setCell(ws, row, COL_D, label, {
    align: "left",
    bordered: false,
  });
  setCell(ws, row, COL_E, options.link ? value || "Add Details" : value ?? "", {
    align: options.valueAlign || "left",
    fill: options.valueFill,
    bordered: false,
    link: options.link,
  });
  applyCellBorders(ws, row, BORDER_PATTERNS.labelValue);
  ws.getRow(row).height = options.height ?? 30;
  return row + 1;
}

function addNotesRow(ws, row, value) {
  setCell(ws, row, COL_D, LABELS.notes, {
    align: "left",
    bordered: false,
  });
  setCell(ws, row, COL_E, value ?? "", {
    align: "left",
    bordered: false,
  });
  applyCellBorders(ws, row, BORDER_PATTERNS.notes);
  ws.getRow(row).height = 30;
}

export function buildPrinciple4Sheet(workbook, sectionC) {
  const ws = workbook.addWorksheet("PRINCIPLE 4");
  setColumnWidths(ws);
  setupSheetView(workbook, ws);
  hideRowsThrough(ws, 5);

  const principle = sectionC?.principle4 || {};
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};
  const integration = leadership?.stakeholderConsultationIntegration || {};

  let row = 8;
  row = addPrincipleTitle(ws, row);
  row = addSectionBand(ws, row, LABELS.essential);

  row = addLabelValueRow(ws, row, LABELS.q1, safeText(essential?.stakeholderGroup), {
    valueFill: VALUE_FILL,
  });

  row = addLabelValueRow(ws, row, LABELS.q2, "Add Details", {
    valueFill: VALUE_FILL,
    valueAlign: "center",
    link: `#'${STAKEHOLDER_GROUP_SHEET}'!D8`,
  });

  row = addSectionBand(ws, row, LABELS.leadership);

  row = addLabelValueRow(
    ws,
    row,
    LABELS.lq1,
    safeText(leadership?.stakeholderConsultationProcesses)
  );

  row = addLabelValueRow(ws, row, LABELS.lq2, toYesNo(integration?.action), {
    valueFill: VALUE_FILL,
    valueAlign: "center",
  });

  row = addLabelValueRow(ws, row, LABELS.lq2Details, safeText(integration?.remark), {
    valueFill: VALUE_FILL,
  });

  row = addLabelValueRow(
    ws,
    row,
    LABELS.lq3,
    safeText(leadership?.vulnerableStakeholderEngagement)
  );

  addNotesRow(ws, row, safeText(principle?.notes || essential?.notes || leadership?.notes));

  return ws;
}
